import {
  Card,
  cn,
  ScrollShadow,
} from '@heroui/react'
import { useMemo } from 'react'
import { Cell, Pie, PieChart as RePieChart, ResponsiveContainer, Tooltip } from 'recharts'

function formatTotal(total) {
  return total >= 1000 ? `${(total / 1000).toFixed(1)}K` : total
}

export default function PieChart({
  data,
  className,
  type,
  isFetching,
  removeContainer,
  activeTag: activeTagFilter,
  ...props
}) {
  const activeTag = useMemo(() => {
    return typeof activeTagFilter === 'string' ? JSON.parse(activeTagFilter) : activeTagFilter
  }, [activeTagFilter])

  // Transform data based on the type (tags, objectives, utterances)
  let chartData = []
  let categories = []

  if (type === 'tags') {
    chartData = data.filter(item => item.tag?.name?.length > 0).map(item => ({
      name: item.tag.name,
      value: item.count,
      color: item.tag.color,
    }))
    categories = data.map(item => item.tag.name)
  }
  else if (type === 'objectives') {
    chartData = data.map(item => ({
      name: item.objective,
      value: item.count,
    }))
    categories = data.map(item => item.objective)
  }
  else if (type === 'utterances') {
    chartData = data.map(item => ({
      name: item.utterance_type,
      value: item.count,
    }))
    categories = data.map(item => item.utterance_type)
  }

  // Helper function to get the appropriate cell color based on data type
  const getCellColor = (entry, index) => {
    if (entry?.color && entry?.color !== '#000000') {
      return entry.color
    }
    // For objectives and utterances, use the HeroUI color system with index
    return `hsl(var(--heroui-${type === 'objectives' ? 'primary' : type === 'tags' ? 'default' : 'warning'}-${(index + 1) * 100}))`
  }

  // Set default title based on type if not provided
  const chartTitle
    = type === 'tags'
      ? 'Pathway Tags'
      : type === 'objectives'
        ? 'Call Objectives'
        : 'Utterance Types'

  const content = (
    <div className="grid h-full grid-cols-2 justify-between gap-2">
      <ResponsiveContainer
        className="[&_.recharts-surface]:outline-hidden"
        width="100%"
      >
        <RePieChart accessibilityLayer margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
          <Tooltip
            content={({ label, payload }) => (
              <div className={`
                flex h-8 min-w-[120px] items-center gap-x-2 rounded-medium
                bg-background px-1 text-tiny shadow-small
              `}
              >
                <span className="font-medium text-foreground">{label}</span>
                {payload?.map((p, index) => {
                  const name = p.name
                  const value = p.value
                  const category = categories?.find(c => c?.toLowerCase() === name?.toLowerCase()) ?? name

                  return (
                    <div
                      key={`${index}-${name}`}
                      className="flex w-full items-center gap-x-2"
                    >
                      <div
                        className="h-2 w-2 flex-none rounded-full"
                        style={{
                          backgroundColor: getCellColor(chartData[index], index),
                        }}
                      />
                      <div className={`
                        flex w-full items-center justify-between gap-x-2 pr-1
                        text-xs text-default-700
                      `}
                      >
                        <span className="text-default-500">{category}</span>
                        <span className="font-mono text-tiny text-default-700">
                          {formatTotal(value)}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
            cursor={false}
          />
          <Pie
            animationDuration={1000}
            animationEasing="ease"
            data={chartData}
            dataKey="value"
            innerRadius="60%"
            nameKey="name"
            paddingAngle={0}
            strokeWidth={0}
          >
            {chartData.map((entry, index) => {
              const isActive = activeTag?.name === entry.name && activeTag?.color === entry.color
              return (
                <Cell
                  key={`cell-${index}`}
                  fill={getCellColor(entry, index)}
                  stroke="#c7ac75"
                  strokeWidth={isActive ? 2 : 0}
                  className={cn({ 'animate-pulse': isActive })}
                />
              )
            })}
          </Pie>
        </RePieChart>
      </ResponsiveContainer>

      <ScrollShadow
        hideScrollBar
        className={cn(`
          flex h-fit max-h-full max-w-full flex-col overflow-y-auto border-l
          border-gray-100 p-2 pr-4
        `)}
      >
        {categories.slice(0, 10).map((category, index) => {
          const isActive = activeTag?.name === category
          return (
            <span
              key={`${category}-${index}`}
              className={cn(`
                flex items-center gap-2 px-2 py-0.5 text-tiny text-default-500
              `, { 'animate-pulse bg-gray-100': isActive })}
            >
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{
                  backgroundColor: getCellColor(chartData[index], index),
                }}
              />
              <span className="truncate">{category?.split('_').join(' ')}</span>
            </span>
          )
        })}
      </ScrollShadow>
    </div>
  )

  if (removeContainer) {
    return content
  }

  return (
    <Card
      className={cn('min-h-[240px] w-full rounded-none border-2 shadow-none', className, { 'animate-pulse': isFetching })}
      {...props}
    >
      <div className={`
        flex w-full items-start justify-items-start gap-x-2 border-b
        border-default-200 bg-default-100 px-3 py-2
      `}
      >
        <dt>
          <h3 className={`
            font-taurus text-tiny font-medium text-default-500 uppercase
          `}
          >
            {chartTitle}
          </h3>
        </dt>
      </div>
      {content}
    </Card>
  )
}
